import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  input,
  OnDestroy,
  output,
} from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { MatIconButton } from '@angular/material/button';
import { MatOption } from '@angular/material/core';
import { MatIcon } from '@angular/material/icon';
import { MatSelect } from '@angular/material/select';
import { MatTooltip } from '@angular/material/tooltip';
import { TranslatePipe } from '@ngx-translate/core';
import {
  ExportConfiguration,
  ExportLanguage,
} from '../../../shared/model/export-payload.model';

@Component({
  selector: 'app-export-bar',
  imports: [
    MatSelect,
    MatOption,
    MatIconButton,
    MatIcon,
    MatTooltip,
    TranslatePipe,
  ],
  templateUrl: './export-bar.component.html',
  styleUrl: './export-bar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
})
export class ExportBarComponent implements AfterViewInit, OnDestroy {
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly document = inject(DOCUMENT);
  private resizeObserver?: ResizeObserver;

  readonly selectedCount = input(0);
  readonly showPreselectionHint = input(false);
  readonly configurations = input<ExportConfiguration[]>([]);
  readonly selectedConfigId = input<string | null>(null);
  readonly language = input<ExportLanguage>('de');
  readonly twoPagerMode = input(false);
  readonly pageCount = input(2);
  readonly includeProfilePicture = input(true);
  readonly running = input(false);

  readonly languageChange = output<ExportLanguage>();
  readonly twoPagerModeChange = output<boolean>();
  readonly pageCountChange = output<number>();
  readonly pictureToggle = output<void>();
  readonly configSelected = output<string>();
  readonly deleteConfig = output<void>();
  readonly download = output<void>();
  readonly sendToTeams = output<void>();
  readonly cancel = output<void>();

  protected readonly languageOptions: readonly ExportLanguage[] = ['de', 'en'];
  protected readonly pageCountOptions: readonly number[] = [1, 2, 3];

  protected chooseLanguage(language: ExportLanguage): void {
    if (language !== this.language()) this.languageChange.emit(language);
  }

  /** Fixierte Seitenelemente (Nach-oben-Knopf) heben sich per --export-bar-height über die Leiste. */
  ngAfterViewInit(): void {
    const bar =
      this.host.nativeElement.querySelector<HTMLElement>('.export-bar');
    if (!bar || typeof ResizeObserver === 'undefined') return;
    const root = this.document.documentElement;
    this.resizeObserver = new ResizeObserver(() => {
      const remPx = parseFloat(getComputedStyle(root).fontSize) || 16;
      root.style.setProperty(
        '--export-bar-height',
        `${bar.getBoundingClientRect().height / remPx}rem`,
      );
    });
    this.resizeObserver.observe(bar);
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.document.documentElement.style.removeProperty('--export-bar-height');
  }
}
